import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

interface Toast {
    id: number;
    message: string;
    type: ToastType | 'confirm';
    onConfirm?: () => void;
    onCancel?: () => void;
}

interface ToastContextType {
    showToast: (message: string, type?: ToastType) => void;
    showConfirm: (message: string, onConfirm: () => void, onCancel?: () => void) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};

interface ToastProviderProps {
    children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
    const [toasts, setToasts] = useState<Toast[]>([]);

    const showToast = useCallback((message: string, type: ToastType = 'info') => {
        const id = Date.now();
        setToasts((prev) => [...prev, { id, message, type }]);

        // Auto remove after 3 seconds
        setTimeout(() => {
            setToasts((prev) => prev.filter((toast) => toast.id !== id));
        }, 3000);
    }, []);

    const showConfirm = useCallback((message: string, onConfirm: () => void, onCancel?: () => void) => {
        const id = Date.now();
        setToasts((prev) => [...prev, { id, message, type: 'confirm', onConfirm, onCancel }]);
    }, []);

    const handleConfirm = (toast: Toast) => {
        if (toast.onConfirm) toast.onConfirm();
        setToasts((prev) => prev.filter((t) => t.id !== toast.id));
    };

    const handleCancel = (toast: Toast) => {
        if (toast.onCancel) toast.onCancel();
        setToasts((prev) => prev.filter((t) => t.id !== toast.id));
    };

    return (
        <ToastContext.Provider value={{ showToast, showConfirm }}>
            {children}
            <div className="toast-container">
                {toasts.map((toast) => (
                    <div key={toast.id} className={`toast toast-${toast.type} fade-in`}>
                        <div className="toast-icon">
                            {toast.type === 'success' && '✅'}
                            {toast.type === 'error' && '❌'}
                            {toast.type === 'info' && 'ℹ️'}
                            {toast.type === 'warning' && '⚠️'}
                            {toast.type === 'confirm' && '❓'}
                        </div>
                        <div className="toast-content flex-1">
                            <div className="toast-message mb-xs">{toast.message}</div>
                            {toast.type === 'confirm' && (
                                <div className="flex gap-xs mt-sm">
                                    <button className="btn btn-primary btn-sm" onClick={() => handleConfirm(toast)}>Sí</button>
                                    <button className="btn btn-outline btn-sm" style={{ background: 'white' }} onClick={() => handleCancel(toast)}>No</button>
                                </div>
                            )}
                        </div>
                        {toast.type !== 'confirm' && (
                            <button className="toast-close" onClick={() => setToasts((prev) => prev.filter((t) => t.id !== toast.id))}>
                                ×
                            </button>
                        )}
                    </div>
                ))}
            </div>
        </ToastContext.Provider>
    );
};
